import React, { useState } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { Spinner } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import atlasEmptyState from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import {
  FormCreationMode,
  QuestionData,
  OPTION_BASED_TYPES,
} from '../../types';
import { QuestionCard } from '../QuestionCard';
import type { QuestionCardCopy } from '../QuestionCard/QuestionCard';
import {
  AddQuestionBottomWrapper,
  AddQuestionButton,
  AddQuestionButtonBottom,
  AddQuestionButtonWrapper,
  EmptyStateContainer,
  EmptyStateCopy,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateLower,
  EmptyStateTitle,
  LoadingStateContainer,
  MinimalCardContent,
  QuestionsContainer,
  QuestionsListContainer,
  QuestionsSectionSubtitle,
  QuestionsSectionTitle,
  SectionContainer,
  SectionHeader,
  SectionHeaderText,
} from './Questions.styles';

const hasConditionalLogic = (questions: QuestionData[]): boolean => {
  return questions.some(
    (q) => q.questionType && OPTION_BASED_TYPES.has(q.questionType),
  );
};

const cleanInvalidNextActions = (questions: QuestionData[]): QuestionData[] => {
  if (questions.length === 0) return questions;

  const questionIdToIndex = new Map(questions.map((q, i) => [q.id, i]));
  let changed = false;

  const updated = questions.map((q, index) => {
    if (!q.options?.length) return q;

    const hasInvalidRef = q.options.some((opt) => {
      if (!opt.nextAction?.startsWith('question:')) return false;
      const targetId = opt.nextAction.slice('question:'.length);
      const targetIndex = questionIdToIndex.get(targetId);
      return targetIndex === undefined || targetIndex <= index + 1;
    });
    if (!hasInvalidRef) return q;

    changed = true;
    return {
      ...q,
      options: q.options.map((opt) => {
        if (!opt.nextAction?.startsWith('question:')) return opt;
        const targetId = opt.nextAction.slice('question:'.length);
        const targetIndex = questionIdToIndex.get(targetId);
        if (targetIndex === undefined || targetIndex <= index + 1) {
          return { ...opt, nextAction: 'next' };
        }
        return opt;
      }),
    };
  });

  return changed ? updated : questions;
};

interface Props {
  mode: FormCreationMode;
  questions: QuestionData[];
  setQuestions: (questions: QuestionData[]) => void;
  questionRefs: React.RefObject<Map<string, HTMLDivElement>>;
  editingLocale?: string;
  questionCardCopy?: QuestionCardCopy;
}

export const Questions = ({
  mode,
  questions,
  setQuestions,
  questionRefs,
  editingLocale,
  questionCardCopy,
}: Props) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [pendingReorderResult, setPendingReorderResult] =
    useState<DropResult | null>(null);
  const [originalQuestionsBeforeReorder, setOriginalQuestionsBeforeReorder] =
    useState<QuestionData[] | null>(null);

  const handleAddQuestion = () => {
    const newQuestion: QuestionData = {
      id: `question-${Date.now()}`,
      questionText: '',
      description: '',
      isRequired: false,
      order: questions.length + 1,
      displayNumber:
        questions.length > 0
          ? Math.max(...questions.map((q) => q.displayNumber)) + 1
          : 1,
      isExpanded: true,
    };
    setQuestions(cleanInvalidNextActions([...questions, newQuestion]));
  };

  const handleDeleteClick = (id: string) => {
    setQuestionToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (questionToDelete) {
      setQuestions(
        cleanInvalidNextActions(
          questions.filter((q) => q.id !== questionToDelete),
        ),
      );
    }
    setDeleteModalOpen(false);
    setQuestionToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setQuestionToDelete(null);
  };

  const handleDuplicateQuestion = (id: string) => {
    const questionToDuplicate = questions.find((q) => q.id === id);
    if (questionToDuplicate) {
      const duplicatedQuestion: QuestionData = {
        ...questionToDuplicate,
        id: `question-${Date.now()}`,
        order: questions.length + 1,
        displayNumber:
          questions.length > 0
            ? Math.max(...questions.map((q) => q.displayNumber)) + 1
            : 1,
      };
      setQuestions(cleanInvalidNextActions([...questions, duplicatedQuestion]));
    }
  };

  const handleQuestionChange = (id: string, data: Partial<QuestionData>) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, ...data } : q)));
  };

  const performReorder = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = [...questions];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedQuestions = items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setQuestions(cleanInvalidNextActions(reorderedQuestions));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    const hasBranching = hasConditionalLogic(questions);

    if (hasBranching) {
      setOriginalQuestionsBeforeReorder([...questions]);
      setPendingReorderResult(result);
      performReorder(result);
      return;
    }

    performReorder(result);
  };

  const handleConfirmReorder = () => {
    setPendingReorderResult(null);
    setOriginalQuestionsBeforeReorder(null);
  };

  const handleCancelReorder = () => {
    if (originalQuestionsBeforeReorder) {
      setQuestions(originalQuestionsBeforeReorder);
    }
    setPendingReorderResult(null);
    setOriginalQuestionsBeforeReorder(null);
  };

  if (mode === 'initial') {
    return (
      <SectionContainer data-testid="questions-section">
        <QuestionsContainer>
          <MinimalCardContent data-testid="questions-initial-state">
            <QuestionsSectionTitle data-testid="questions-initial-title">
              Questions
            </QuestionsSectionTitle>
          </MinimalCardContent>
        </QuestionsContainer>
      </SectionContainer>
    );
  }

  if (mode === 'loading') {
    return (
      <SectionContainer data-testid="questions-section">
        <QuestionsContainer>
          <LoadingStateContainer data-testid="questions-loading-state">
            <Spinner variant="secondary" size="xlarge" />
          </LoadingStateContainer>
        </QuestionsContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer data-testid="questions-section">
      <QuestionsContainer data-testid="questions-card-content">
        <SectionHeader data-testid="questions-section-header">
          <SectionHeaderText>
            <QuestionsSectionTitle data-testid="questions-section-title">
              Questions
            </QuestionsSectionTitle>
            <QuestionsSectionSubtitle data-testid="questions-section-subtitle">
              Add questions to your form
            </QuestionsSectionSubtitle>
          </SectionHeaderText>
          <AddQuestionButtonWrapper>
            <AddQuestionButton
              variant="secondary"
              size="medium"
              onClick={handleAddQuestion}
              data-testid="add-question-button"
            >
              <StyledIconInButton
                size="medium"
                position="left"
                className="uil uil-plus"
              />
              Add question
            </AddQuestionButton>
          </AddQuestionButtonWrapper>
        </SectionHeader>

        {questions.length === 0 ? (
          <EmptyStateContainer data-testid="questions-empty-state">
            <EmptyStateIllustration
              src={atlasEmptyState}
              alt=""
              width={160}
              height={132}
              data-testid="questions-empty-state-illustration"
            />
            <EmptyStateLower>
              <EmptyStateCopy data-testid="questions-empty-state-text">
                <EmptyStateTitle>No questions added yet</EmptyStateTitle>
                <EmptyStateDescription>
                  Add your first question
                </EmptyStateDescription>
              </EmptyStateCopy>
              <AddQuestionButtonBottom
                variant="primary"
                size="medium"
                onClick={handleAddQuestion}
                data-testid="add-first-question-button"
                noMinWidth
              >
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-plus"
                />
                Add question
              </AddQuestionButtonBottom>
            </EmptyStateLower>
          </EmptyStateContainer>
        ) : (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <QuestionsListContainer
                    data-testid="questions-list"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {questions.map((question, index) => (
                      <QuestionCard
                        key={question.id}
                        ref={(el) => {
                          if (el) questionRefs.current?.set(question.id, el);
                          else questionRefs.current?.delete(question.id);
                        }}
                        question={question}
                        index={index}
                        isLastQuestion={index === questions.length - 1}
                        allQuestions={questions}
                        onDelete={handleDeleteClick}
                        onDuplicate={handleDuplicateQuestion}
                        onChange={handleQuestionChange}
                        editingLocale={editingLocale}
                        uiCopy={questionCardCopy}
                        data-testid={`question-card-${index}`}
                      />
                    ))}
                    {provided.placeholder}
                  </QuestionsListContainer>
                )}
              </Droppable>
            </DragDropContext>
            <AddQuestionBottomWrapper>
              <AddQuestionButtonBottom
                variant="tertiary"
                size="medium"
                onClick={handleAddQuestion}
                data-testid="add-question-button-bottom"
                noMinWidth
              >
                <StyledIconInButton
                  size="medium"
                  position="left"
                  className="uil uil-plus"
                />
                Add question
              </AddQuestionButtonBottom>
            </AddQuestionBottomWrapper>
          </>
        )}
      </QuestionsContainer>

      <Dialog
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-question-dialog-title"
        aria-describedby="delete-question-dialog-description"
        data-testid="delete-question-modal"
      >
        <DialogTitle component="div">
          <Typography
            variant="h6"
            component="h2"
            id="delete-question-dialog-title"
          >
            Confirm deletion
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleCancelDelete}
            size="small"
            color="inherit"
            data-testid="delete-question-modal-close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-question-dialog-description">
            This action can&apos;t be undone and all settings will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleCancelDelete}
            data-testid="delete-question-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="destructive"
            onClick={handleConfirmDelete}
            autoFocus
            data-testid="delete-question-modal-confirm"
          >
            Delete Question
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingReorderResult}
        onClose={handleCancelReorder}
        aria-labelledby="branching-warning-dialog-title"
        aria-describedby="branching-warning-dialog-description"
        data-testid="branching-warning-modal"
      >
        <DialogTitle component="div">
          <Typography
            variant="h6"
            component="h2"
            id="branching-warning-dialog-title"
          >
            Review branching logic
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleCancelReorder}
            size="small"
            color="inherit"
            data-testid="branching-warning-modal-close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="branching-warning-dialog-description">
            Reordering this question may affect the form&apos;s conditional
            logic and how other questions are displayed. Please review the
            branching logic before publishing.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleCancelReorder}
            data-testid="branching-warning-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReorder}
            autoFocus
            data-testid="branching-warning-modal-confirm"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </SectionContainer>
  );
};
